<template>
  <VCalendarDatePicker
    v-model="_value"
    :popover="{ visibility: (!disabled) ? 'click' : 'hidden' }"
    :show-popover="!disabled"
    :theme="{ bgAccentHigh: { light: 'bookapp-vc-bg-accent-high' } }"
    :min-date="minDate"
    :input-props="{ class: 'form-control', disabled, placeholder }"
    :locale="locale"
    @dayclick="
      (day, event) => {
        event.target.blur();
      }
    "
  >
    <template #default="{ inputValue, inputEvents }">
      <UInput
        :modelValue="inputValue"
        :disabled="disabled"
        :placeholder="placeholder"
        size="lg"
        @click="inputEvents.click"
        @keypress="(e) => e.preventDefault()"
        @keydown="(e) => e.preventDefault()"
        @paste="(e) => e.preventDefault()"
      >
        <template #trailing>
          <b-icon icon="calendar" />
        </template>
      </UInput>
    </template>
  </VCalendarDatePicker>
</template>

<script lang="ts">
export default {
  name: 'BDateInput'
}
</script>

<script setup lang="ts">
import { DatePicker as VCalendarDatePicker } from '@angelblanco/v-calendar'

import '@angelblanco/v-calendar/dist/style.css'

const augmenters = {
  YMD: {
    input: (value) => {
      if (value && !(value instanceof Date)) {
        if (value.length == 8) {
          const year = value.substring(0, 4)
          const month = value.substring(4, 6)
          const day = value.substring(6, 8)

          return new Date(year, month - 1, day, 0, 0, 0, 0)
        }
      }
      return value
    },
    output: (value) => {
      if (value && value instanceof Date) {
        const zeroPad = (num) => {
          if (num < 10) return '0' + num.toString()
          return num.toString()
        }

        return value.getFullYear().toString() + zeroPad(value.getMonth() + 1) + zeroPad(value.getDate())
      }
      return value
    }
  },
  timestamp: {
    input: (value) => {
      if (value && !(value instanceof Date)) return new Date(value * 1000)
      return value
    },
    output: (value) => {
      if (value && value instanceof Date) {
        const offset = value.getTimezoneOffset() * 60000
        const date = value.getTime() - offset
        return parseInt(date / 1000)
      }
      return value
    }
  }
}

const props = defineProps({
  minDate: {
    type: Date,
    required: false,
    default: null
  },
  augmenter: {
    type: String,
    required: false,
    default: null
  },
  placeholder: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const model = defineModel()
const { $site } = useNuxtApp()

const _augmenter = computed(() => {
  if (props.augmenter && augmenters[props.augmenter]) {
    return augmenters[props.augmenter]
  }

  return null
})

const _value = computed({
  get: () => {
    if (model.value === '') return null

    if (_augmenter.value) {
      return _augmenter.value.input(model.value)
    } else {
      return model.value
  }
  },
  set: (value) => {
    if (_augmenter.value) {
      model.value = _augmenter.value.output(value)
    } else {
      model.value = value
    }
  }
})

const locale = computed(() => {
  const locale = $site.location.locale
  return locale.replace('_', '-')
})
</script>

<style>
.bookapp-vc-bg-accent-high {
  background-color: var(--bookapp-action-tint-color)
}

.vc-popover-content-wrapper {
  z-index: 1000;
}
</style>
